// Currency conversion utilities with fixed demo rates

export interface CurrencyRates {
  [key: string]: number;
}

// Fixed conversion rates relative to USD (for demo purposes)
export const DEMO_CURRENCY_RATES: CurrencyRates = {
  USD: 1.0,
  EUR: 0.85,
  GBP: 0.73,
  CAD: 1.35,
  AUD: 1.52,
  JPY: 110.0,
};

export const CURRENCY_SYMBOLS: { [key: string]: string } = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  CAD: "C$",
  AUD: "A$",
  JPY: "¥",
};

export const CURRENCY_NAMES: { [key: string]: string } = {
  USD: "US Dollar",
  EUR: "Euro",
  GBP: "British Pound",
  CAD: "Canadian Dollar",
  AUD: "Australian Dollar",
  JPY: "Japanese Yen",
};

/**
 * Convert amount from one currency to another
 */
export function convertCurrency(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  rates: CurrencyRates = DEMO_CURRENCY_RATES,
): number {
  if (fromCurrency === toCurrency) return amount;

  const fromRate = rates[fromCurrency] || 1;
  const toRate = rates[toCurrency] || 1;

  // Convert to USD first, then to target currency
  const usdAmount = amount / fromRate;
  return usdAmount * toRate;
}

/**
 * Format currency amount with proper symbol and formatting
 */
export function formatCurrencyAmount(
  amount: number,
  currency: string,
  showSymbol: boolean = true,
): string {
  const symbol = CURRENCY_SYMBOLS[currency] || currency;

  // JPY should be displayed as whole numbers (grouped) when symbol shown
  if (currency === "JPY") {
    const rounded = Math.round(amount);
    const formattedAmount = rounded.toLocaleString();
    return showSymbol ? `${formattedAmount}${symbol}` : String(rounded);
  }

  // For unit tests and many programmatic uses we keep the raw numeric string without grouping.
  // This keeps formatCurrencyAmount deterministic (no locale-dependent grouping).
  const numeric = amount.toFixed(2);
  return showSymbol ? `${symbol}${numeric}` : numeric;
}

/**
 * Get currency symbol for a given currency code
 */
export function getCurrencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency] || currency;
}

/**
 * Get display amount for a subscription in the display currency
 */
export function getDisplayAmount(
  amount: number,
  originalCurrency: string,
  displayCurrency: string,
  rates: CurrencyRates = DEMO_CURRENCY_RATES,
): {
  amount: number;
  formatted: string;
  isConverted: boolean;
} {
  const isConverted = originalCurrency !== displayCurrency;
  const displayAmount = isConverted
    ? convertCurrency(amount, originalCurrency, displayCurrency, rates)
    : amount;

  return {
    amount: displayAmount,
    formatted: formatCurrencyAmount(displayAmount, displayCurrency),
    isConverted,
  };
}

/**
 * Convert frequency-based amounts to a common basis for comparison
 */
export function normalizeAmountToMonthly(
  amount: number,
  frequency:
    | "monthly"
    | "yearly"
    | "weekly"
    | "custom"
    | "fortnightly"
    | "every_2_months"
    | "quarterly",
  customFrequency?: number,
): number {
  switch (frequency) {
    case "weekly":
      return amount * 4.33; // Average weeks per month
    case "fortnightly":
      return amount * 2.17; // Average fortnights per month
    case "monthly":
      return amount;
    case "every_2_months":
      return amount / 2;
    case "quarterly":
      return amount / 3;
    case "yearly":
      return amount / 12;
    case "custom":
      if (customFrequency) {
        return (amount / customFrequency) * 30; // Assume custom is in days
      }
      return amount;
    default:
      return amount;
  }
}

/**
 * Unified currency formatting function - main facade for currency formatting in the application
 * Combines formatting, conversion, and display in one convenient function
 */
export function formatCurrency(
  amount: number,
  currencyCode: string,
  options?: {
    showSymbol?: boolean;
    useFX?: boolean;
    displayCurrency?: string;
  },
): string {
  const showSymbol = options?.showSymbol ?? true;

  if (options?.useFX) {
    const targetCurrency = options.displayCurrency || "EUR";
    const convertedAmount = convertCurrency(
      amount,
      currencyCode,
      targetCurrency,
      DEMO_CURRENCY_RATES,
    );

    if (!showSymbol) {
      // Programmatic/non-UI formatting: return ungrouped numeric string
      return targetCurrency === "JPY"
        ? String(Math.round(convertedAmount))
        : convertedAmount.toFixed(2);
    }

    // UI-facing formatting for JPY: tests expect no grouping separators (raw integer)
    if (targetCurrency === "JPY") {
      const rounded = Math.round(convertedAmount);
      return `${rounded}${CURRENCY_SYMBOLS[targetCurrency] || targetCurrency}`;
    }

    const grouped = convertedAmount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${CURRENCY_SYMBOLS[targetCurrency] || targetCurrency}${grouped}`;
  }

  // No FX
  if (!showSymbol) {
    return currencyCode === "JPY"
      ? String(Math.round(amount))
      : amount.toFixed(2);
  }

  // UI-facing formatting for JPY: no grouping separators (raw integer expected by tests)
  if (currencyCode === "JPY") {
    const rounded = Math.round(amount);
    return `${rounded}${CURRENCY_SYMBOLS[currencyCode] || currencyCode}`;
  }

  const grouped = amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${CURRENCY_SYMBOLS[currencyCode] || currencyCode}${grouped}`;
}

/**
 * Get all supported currencies
 */
export function getSupportedCurrencies() {
  return Object.keys(DEMO_CURRENCY_RATES).map((code) => ({
    code,
    name: CURRENCY_NAMES[code],
    symbol: CURRENCY_SYMBOLS[code],
  }));
}
